import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requiredField, useForm } from '../../../../../utils/validators';

import { ModerationStatuses, ModerationFlowsModel } from '../../../../../models';

import { Button, Card, ContentCard, Form, ToggleContainer, HeaderCard, SelectLabel, SubTitleCard, TitleCard, WeezHeader, ToastContext, Message } from '@weezevent/nacre';

import { attendeeStatusFilterOptions } from '../../../../../constants/moderation';
import LoadingCard from '../../../../../components/loadingCard';

import Styles from '../statusesTransitions.module.css';

export default function StatusDetails({ organization, flowId, statusId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [formStatus, setFormStatus, setFormStatusObject, formErrors, formValidate] = useForm(
        {
            name: '',
            stage: '',
            is_default_status: false
        },
        {
            name: requiredField,
            stage: requiredField
        }
    );

    const [moderationStatus, loadingModerationStatus] = ModerationStatuses.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            id: statusId,
            launch: Boolean(statusId),
            query: {
                with_annotated_count: true
            },
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                setFormStatusObject({ ...formStatus, ...response.object });
            }
        },
        [organization, flowId, statusId]
    );
    const handleSubmit = React.useCallback(() => {
        let status = new ModerationStatuses({ ...formStatus, flow_id: flowId });
        status
            .save({ organizationId: organization.id, flowId })
            .then(({ response }) => {
                let flow = new ModerationFlowsModel({ id: flowId });
                flow.default_status = formStatus.is_default_status ? response.id : null;
                if (!statusId && !formStatus.is_default_status) {
                    // Prevent sending default_status null to flow when creating
                    delete flow.default_status;
                }
                flow.save({ organizationId: organization.id }).then(() => {
                    toast.success(t(`ticket.moderation.statusesTransitions.statuses.detail.toastSuccess`));
                    history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/statuses`);
                });
            })
            .catch(() => {
                toast.error(t('ticket.moderation.statusesTransitions.statuses.detail.toastError'));
            });
    }, [formStatus, organization.id, flowId]);

    React.useEffect(() => {
        formValidate();
    }, [formStatus]);

    return (
        <>
            <WeezHeader
                title={!statusId ? t('ticket.moderation.statusesTransitions.statuses.detail.titleAdd') : t('ticket.moderation.statusesTransitions.statuses.detail.titleEdit')}
                rightComponent={[<Button key={0} primary label={t('ticket.common.action.save')} disabled={Object.keys(formErrors).length} onClick={() => handleSubmit()} />]}
                backLink={{
                    onClick: () => {
                        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/statuses`);
                    }
                }}
            />
            <Card>
                <HeaderCard separator>
                    <TitleCard title={t('ticket.moderation.statusesTransitions.statuses.detail.settingsCardTitle')} />
                    <SubTitleCard title={t('ticket.moderation.statusesTransitions.statuses.detail.settingsCardSubtitle')} />
                </HeaderCard>
                <LoadingCard loading={statusId && loadingModerationStatus}>
                    <ContentCard>
                        <Form.Field>
                            <Form.Label label={t('ticket.moderation.statusesTransitions.statuses.detail.nameLabel')} />
                            <Form.Input
                                name="name"
                                placeholder={t('ticket.moderation.statusesTransitions.statuses.detail.namePlaceholder')}
                                value={formStatus?.name}
                                onChange={({ target: { value } }) => {
                                    setFormStatus('name', value);
                                }}
                            />
                        </Form.Field>
                        <Form.Field>
                            <SelectLabel
                                name="stage"
                                value={formStatus?.stage}
                                label={t('ticket.moderation.statusesTransitions.statuses.detail.typeLabel')}
                                onChange={value => setFormStatus('stage', value)}
                                options={attendeeStatusFilterOptions().map(option => ({ ...option, value: option.name }))}
                                placeholder={t('ticket.common.action.select')}
                                disabled={Boolean(moderationStatus?.attendees_count > 0)}
                            />
                            <Message
                                title={t('ticket.moderation.statusesTransitions.statuses.detail.typeMessageTitle')}
                                type="default"
                                visible={moderationStatus?.attendees_count > 0}
                            >
                                {t('ticket.moderation.statusesTransitions.statuses.detail.typeMessageDescription')}
                            </Message>
                        </Form.Field>
                        <Form.Field>
                            <ToggleContainer
                                title={t('ticket.moderation.statusesTransitions.statuses.detail.defaultStatusLabel')}
                                subtitle={t('ticket.moderation.statusesTransitions.statuses.detail.defaultStatusSubtitle')}
                                onChange={({ target: { checked } }) => setFormStatus('is_default_status', checked)}
                                checked={formStatus?.is_default_status}
                                className={Styles['toggle-container']}
                            />
                        </Form.Field>
                    </ContentCard>
                </LoadingCard>
            </Card>
        </>
    );
}
